import React, {FC, useState, createContext, useEffect} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {useQuery} from '@tanstack/react-query';
import {AxiosError} from 'axios';

import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {ENDPOINTS} from 'constants/endpoints';
import {KLARNA_CONTAINER_ID, KLARNA_SDK_URL} from 'constants/klarna';
import {
    KlarnaAddressType,
    KlarnaContextType,
    KlarnaPaymentResponseType,
} from 'desktop/types/klarnaTypes';
import {useLoadScriptWithCheck} from 'hooks/useLoadScript/useLoadScript';
import {apiConfig} from 'libs/api/api';

import {initKlarna} from 'libs/klarna/klarna';

import {PAYMENTS} from '../../constants/paymentStep';

import {useKlarnaAuthorize} from './helper/useKlarnaAutorize';

export type OnAuthorizeCallbackType = (data: {
    paymentId: string;
    token: string;
    method: string;
}) => void;

export type KlarnaPropsType = {
    orderLines: Array<{
        name: string;
        quantity: number;
        unitPrice: number;
        totalDiscountAmount: number;
        totalTaxAmount: number;
        taxRate: number;
        imageUrl?: string;
        productUrl?: string;
        type: string;
    }>;
    shippingAddress: KlarnaAddressType;
    billingAddress: KlarnaAddressType;
    amount: number;
    orderTaxAmount: number;
    onAuthorizeCallback: OnAuthorizeCallbackType;
    isPaymentStep?: boolean;
};

export const KlarnaContext = createContext<KlarnaContextType>({
    anchor: null,
    authorize: () => {},
    isEmptyCategories: false,
    isError: false,
    isLoading: false,
    title: null,
});

/**
 * Klarna context provider data
 *
 * @param {*} children - children component
 * @returns {FC} Context Provider
 */
export const KlarnaProvider: FC<KlarnaPropsType & {children: React.ReactNode}> = ({
    amount,
    billingAddress,
    children,
    isPaymentStep,
    onAuthorizeCallback,
    orderLines,
    orderTaxAmount,
    shippingAddress,
}) => {
    const [state, setState] = useState({
        isEmptyCategories: false,
        paymentMethod: '',
        sessionId: '',
        title: '',
    });
    const isLoaded = useLoadScriptWithCheck(KLARNA_SDK_URL, 'Klarna', {remount: true});

    const isEnabled = isLoaded && Boolean(amount);

    const {
        data,
        error,
        isError,
        isInitialLoading: isLoading,
        refetch,
    } = useQuery({
        enabled: isEnabled,
        queryFn: () => {
            return apiConfig.instanceGusaBackend.request<KlarnaPaymentResponseType>({
                baseURL: apiConfig.API_URL,
                data: {
                    payment: {
                        amount,
                        billingAddress,
                        orderLines,
                        orderTaxAmount,
                        shippingAddress,
                    },
                },
                method: 'POST',
                url: ENDPOINTS.CREATE_KLARNA_SESSION,
            });
        },
        queryKey: [PAYMENTS.METHOD_KLARNA, amount],
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        retry: false,
        retryOnMount: true,
    });

    useEffect(() => {
        if (data) {
            const {clientToken, paymentMethodCategories, sessionId} = data.data;
            if (!paymentMethodCategories || paymentMethodCategories.length === 0) {
                setState(prev => ({
                    ...prev,
                    isEmptyCategories: true,
                }));
                return;
            }
            const [{identifier, name}] = paymentMethodCategories;

            initKlarna(clientToken, identifier);

            setState({
                isEmptyCategories: false,
                paymentMethod: identifier,
                sessionId,
                title: name,
            });
        }
    }, [data]);

    useEffect(() => {
        if (!error) return;
        const {response} = error as AxiosError<{title: string}>;
        const errorTitle = response?.data?.title || '';
        if (errorTitle)
            sendDesktopAnalyticsEvent(
                CHECKOUT_PAYMENT_STEP,
                {
                    message: `Error - Create session error - ${errorTitle}`,
                    method: PAYMENTS.METHOD_KLARNA,
                },
                false,
            );
    }, [error]);

    useEffect(() => {
        if (isPaymentStep && isEnabled) refetch();
    }, [isPaymentStep, isEnabled]);

    const authorize = useKlarnaAuthorize(state.paymentMethod, state.sessionId, onAuthorizeCallback);

    const value = {
        anchor: <div id={KLARNA_CONTAINER_ID} />,
        authorize,
        isEmptyCategories: state.isEmptyCategories,
        isError,
        isLoading,
        title: state.title,
    };

    return <KlarnaContext.Provider value={value}>{children}</KlarnaContext.Provider>;
};
